/**
 * O texto do documento como o DOCX o escreveu (E202.F1, GUS-145): cada trecho com negrito, itálico,
 * fonte mono, cor, fundo (o selo de situação) e corpo da letra relativo ao texto corrido; os
 * parágrafos com o alinhamento e as imagens; os itens vizinhos numa lista. Na célula de tabela, o
 * justificado sai à esquerda: na coluna estreita da tela ele abre buracos entre as palavras.
 */
import type { CSSProperties } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { Alinhamento, ParagrafoDoBlocoData, TrechoDoBlocoData } from '@/lib/documentoDaSaida';

import { CORPO_PT } from './paleta';

const t = ptBR.documentoDaMesa;

const ALINHAMENTO: Record<Alinhamento, CSSProperties['textAlign']> = {
  esquerda: 'left',
  centro: 'center',
  direita: 'right',
  justificado: 'justify',
};

function estiloDoTrecho(trecho: TrechoDoBlocoData): CSSProperties {
  return {
    fontWeight: trecho.negrito ? 600 : undefined,
    fontStyle: trecho.italico ? 'italic' : undefined,
    color: trecho.cor !== null ? `#${trecho.cor}` : undefined,
    backgroundColor: trecho.fundo !== null ? `#${trecho.fundo}` : undefined,
    fontSize: trecho.tamanho !== null ? `${(trecho.tamanho / CORPO_PT).toFixed(3)}em` : undefined,
    verticalAlign: trecho.sobrescrito ? 'super' : undefined,
  };
}

function Trecho({ trecho }: { trecho: TrechoDoBlocoData }): JSX.Element {
  const classes = [
    trecho.mono ? 'font-mono' : '',
    trecho.fundo !== null ? 'px-1 py-px' : '',
    'whitespace-pre-wrap break-words',
  ];
  return (
    <span className={classes.join(' ').trim()} style={estiloDoTrecho(trecho)}>
      {trecho.texto}
    </span>
  );
}

function alinhamentoDo(paragrafo: ParagrafoDoBlocoData, naCelula: boolean) {
  if (paragrafo.alinhamento === null) return undefined;
  if (naCelula && paragrafo.alinhamento === 'justificado') return 'left';
  return ALINHAMENTO[paragrafo.alinhamento];
}

export function ParagrafoDoDocumento({
  paragrafo,
  className,
  naCelula = false,
}: {
  paragrafo: ParagrafoDoBlocoData;
  className?: string;
  naCelula?: boolean;
}): JSX.Element {
  const alinhamento = alinhamentoDo(paragrafo, naCelula);
  return (
    <p className={className} style={{ textAlign: alinhamento }}>
      {paragrafo.trechos.map((trecho, i) => (
        <Trecho key={i} trecho={trecho} />
      ))}
      {paragrafo.imagens.map((imagem, i) => (
        <img
          key={`imagem-${i}`}
          src={imagem.src}
          alt={t.imagem}
          width={imagem.largura}
          height={imagem.altura}
          className="block h-auto max-w-full"
          // A largura impressa é fração da largura do texto da página, e não da célula em que a
          // imagem está: `cqw` mede pelo papel (o `article` é o contêiner).
          style={{ width: `${((imagem.largura_no_papel ?? 1) * 100).toFixed(1)}cqw` }}
          data-testid="documento-imagem"
        />
      ))}
    </p>
  );
}

/** Os parágrafos na ordem, com os itens vizinhos numa lista. */
export function ParagrafosDoDocumento({
  paragrafos,
  naCelula = false,
}: {
  paragrafos: readonly ParagrafoDoBlocoData[];
  naCelula?: boolean;
}): JSX.Element {
  const grupos: ParagrafoDoBlocoData[][] = [];
  for (const p of paragrafos) {
    const ultimo = grupos.at(-1);
    if (p.item && ultimo !== undefined && ultimo[0]?.item === true) ultimo.push(p);
    else grupos.push([p]);
  }
  return (
    <>
      {grupos.map((grupo, i) =>
        grupo[0]?.item === true ? (
          <ul key={i} className="my-1 list-disc space-y-0.5 pl-5">
            {grupo.map((p, j) => (
              <li key={j}>
                <ParagrafoDoDocumento paragrafo={p} naCelula={naCelula} />
              </li>
            ))}
          </ul>
        ) : (
          <ParagrafoDoDocumento
            key={i}
            paragrafo={grupo[0] as ParagrafoDoBlocoData}
            className="my-1"
            naCelula={naCelula}
          />
        ),
      )}
    </>
  );
}
